import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { TRENDYOL_CANCEL_REASONS, getCancelReasons } from '../services/TrendyolYemekService';
import type { TrendyolOrder } from '../types';

interface TrendyolCancelModalProps {
  open: boolean;
  onClose: () => void;
  order: TrendyolOrder | null;
  onConfirm: (packageId: string, itemIdList: string[], reasonId: number) => Promise<boolean>;
}

const TrendyolCancelModal: React.FC<TrendyolCancelModalProps> = ({
  open,
  onClose,
  order,
  onConfirm,
}) => {
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancelReasons = useMemo(
    () => getCancelReasons(order?.deliveryType || 'GO'),
    [order?.deliveryType]
  );

  const allItemIds = useMemo(() => {
    if (!order) return [];
    const ids: string[] = [];
    for (const line of order.lines) {
      for (const item of line.items) {
        if (item.packageItemId && !item.isCancelled) {
          ids.push(item.packageItemId);
        }
      }
    }
    return ids;
  }, [order]);

  const handleSelectAll = () => {
    if (selectedItems.size === allItemIds.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(allItemIds));
    }
  };

  const toggleItem = (itemId: string) => {
    const next = new Set(selectedItems);
    if (next.has(itemId)) {
      next.delete(itemId);
    } else {
      next.add(itemId);
    }
    setSelectedItems(next);
  };

  const handleConfirm = async () => {
    if (!order || selectedReasonId === null) return;
    if (selectedItems.size === 0) {
      setError('En az bir ürün seçmelisiniz');
      return;
    }

    setLoading(true);
    setError(null);

    const success = await onConfirm(order.id, Array.from(selectedItems), selectedReasonId);
    setLoading(false);

    if (success) {
      handleClose();
    } else {
      setError('İptal işlemi başarısız oldu');
    }
  };

  const handleClose = () => {
    if (loading) return;
    setSelectedReasonId(null);
    setSelectedItems(new Set());
    setError(null);
    onClose();
  };

  if (!order) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Uber Eats Sipariş İptal Et</Typography>
        <Typography variant="body2" color="text.secondary">
          Sipariş: {order.orderCode || order.orderNumber}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* İptal Nedeni */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>İptal Nedeni</Typography>
        <RadioGroup
          value={selectedReasonId ?? ''}
          onChange={(e) => setSelectedReasonId(Number(e.target.value))}
        >
          {cancelReasons.map((reason) => (
            <FormControlLabel
              key={reason.id}
              value={reason.id}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  {reason.label}
                  {reason.model1Only && (
                    <Typography component="span" variant="caption" color="text.secondary"> (Sadece Model 1)</Typography>
                  )}
                </Typography>
              }
            />
          ))}
        </RadioGroup>

        <Divider sx={{ my: 2 }} />

        {/* Ürün Seçimi */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">İptal Edilecek Ürünler</Typography>
          <Button size="small" onClick={handleSelectAll}>
            {selectedItems.size === allItemIds.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
          </Button>
        </Box>
        <FormGroup>
          {order.lines.map((line, lineIdx) => (
            line.items.filter(item => !item.isCancelled).map((item, itemIdx) => (
              <FormControlLabel
                key={`${lineIdx}-${itemIdx}`}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedItems.has(item.packageItemId)}
                    onChange={() => toggleItem(item.packageItemId)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{line.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {line.unitSellingPrice?.toFixed(2)} TL
                    </Typography>
                  </Box>
                }
              />
            ))
          ))}
        </FormGroup>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Vazgeç</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading || selectedReasonId === null || selectedItems.size === 0}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {loading ? 'İptal Ediliyor...' : 'Siparişi İptal Et'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrendyolCancelModal;
